import { memo, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SysTable from "app/class/sys-table";
import { LogicalNode } from "app/types/record-management-types";

import SysColumn from "app/class/sys-column";
import { useTables } from "app/hooks/useTables";
import { useColumns } from "app/hooks/useColumns";
import { useQueryClient } from "@tanstack/react-query";
import RecordListProvider from "./RecordListProvider";
import RecordListNavbarComponent from "./RecordListNavbar";
import { Box, Paper } from "@mui/material";
import RecordListTableComponent from "./RecordListTable";
import RecordListColumnManagerComponent from "./RecordListColumnManager";
import { RelatedListHeader } from "./RelatedListHeader";
import queryKeys from "shared/services/queryKeys";
import Translate from "component/global/translation/translation-component";
import { usePageTitle } from "app/hooks/usePageTitle";
import RecordListSkeletonComponent from "./record-list-views/RecordListSkeletonComponent";
import { SortPreferences, useRecordListSort } from "../hooks/useRecordListSort";
import { useColumnPreferences } from "../hooks/useColumnPreferences";
import {
    combineRecordListFilters,
    deserializeFilterFromParam,
    deserializeSortFromParam,
    getRecordListFilterParamKey,
    getRecordListSortParamKey,
    serializeFilterToParam,
    serializeSortToParam,
} from "../services/recordListUrlState";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { useTranslationsReady } from "app/providers/TranslationProvider";
import { useRecordListContext } from "../hooks/useRecordListContext";

interface RecordListProps {
    paramTableName?: string;
    queryParams?: LogicalNode;
    isRelatedList?: boolean;
    headerContent?: ReactNode;
    columnPreferenceScope?: string;
    actions?(params: any, table: SysTable): JSX.Element[];
    readOnly?: boolean;
    columnLabels?: Record<string, string>;
}

const RecordListComponent = ({
    paramTableName,
    queryParams,
    actions,
    readOnly = false,
    columnLabels,
    headerContent,
    columnPreferenceScope,
    isRelatedList = false
}: RecordListProps) => {
    const { t } = useTranslation();
    const translationsReady = useTranslationsReady();
    const { tableName } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const pendingSearchParamUpdatesRef = useRef<Record<string, string | null>>({});
    const searchParamUpdateScheduledRef = useRef(false);
    const queryClient = useQueryClient();
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [toolbarHeight, setToolbarHeight] = useState(49);

    useLayoutEffect(() => {
        if (isRelatedList || !toolbarRef.current) return;
        const toolbar = toolbarRef.current;
        const measure = () => setToolbarHeight(toolbar.getBoundingClientRect().height + 8);
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(toolbar);
        return () => observer.disconnect();
    }, [isRelatedList]);

    const { data: tables = [], isLoading } = useTables();
    const { data: columns = [] } = useColumns();

    /**
     * 1. Resolve table
     */
    const currentTable = useMemo<SysTable | null>(() => {
        const name = paramTableName ?? tableName;
        return tables.find(t => t.name === name) ?? null;
    }, [tables, tableName, paramTableName]);


    const pageTitle = useMemo(() => {
        return !isRelatedList && currentTable ? `${t("List for")} ${translateMetadataLabel(t, currentTable)}` : undefined;
    }, [currentTable, isRelatedList, t]);
    usePageTitle(pageTitle, {
        id: `record-list:${currentTable?.name || paramTableName || tableName || "unknown"}`,
        enabled: translationsReady && !isRelatedList && Boolean(pageTitle),
        priority: 50,
    });

    const tableColumns = useMemo<SysColumn[]>(() => {
        if (!currentTable) {
            return [];
        }

        return columns
            .filter((column) => column.sys_table_id === currentTable.id)
            .sort((a, b) => a.order - b.order);
    }, [columns, currentTable]);

    const resolvedColumnPreferenceScope = columnPreferenceScope || (isRelatedList ? "related" : "list");
    const columnPreferences = useColumnPreferences(
        currentTable?.name || "",
        tableColumns,
        resolvedColumnPreferenceScope,
        tables.some(table => table.parent_table_name === currentTable?.name)
    );

    const filterParam = searchParams.get(getRecordListFilterParamKey());
    const sortParam = searchParams.get(getRecordListSortParamKey());

    const appliedFilter = useMemo(
        () => (!isRelatedList ? deserializeFilterFromParam(filterParam) : null),
        [filterParam, isRelatedList]
    );

    const urlSortState = useMemo(
        () => deserializeSortFromParam(!isRelatedList ? sortParam : null),
        [isRelatedList, sortParam]
    );

    const effectiveQueryParams = useMemo(
        () => combineRecordListFilters(queryParams, appliedFilter),
        [appliedFilter, queryParams]
    );

    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        pendingSearchParamUpdatesRef.current = {
            ...pendingSearchParamUpdatesRef.current,
            ...updates,
        };

        if (searchParamUpdateScheduledRef.current) {
            return;
        }

        searchParamUpdateScheduledRef.current = true;

        queueMicrotask(() => {
            const pendingUpdates = pendingSearchParamUpdatesRef.current;
            pendingSearchParamUpdatesRef.current = {};
            searchParamUpdateScheduledRef.current = false;

            setSearchParams((currentParams) => {
                const nextParams = new URLSearchParams(currentParams);

                Object.entries(pendingUpdates).forEach(([key, value]) => {
                    if (value === null) {
                        nextParams.delete(key);
                        return;
                    }

                    nextParams.set(key, value);
                });

                return nextParams;
            }, { replace: true });
        });
    }, [setSearchParams]);

    const handleApplyFilter = useCallback((filter: LogicalNode | null) => {
        if (isRelatedList) {
            return;
        }

        updateSearchParams({
            [getRecordListFilterParamKey()]: serializeFilterToParam(filter),
        });
    }, [isRelatedList, updateSearchParams]);

    const handleClearFilter = useCallback(() => {
        if (isRelatedList) {
            return;
        }

        updateSearchParams({
            [getRecordListFilterParamKey()]: null,
        });
    }, [isRelatedList, updateSearchParams]);

    const handleSortPreferenceChange = useCallback((sortPreference: SortPreferences | null) => {
        if (isRelatedList) {
            return;
        }

        updateSearchParams({
            [getRecordListSortParamKey()]: serializeSortToParam(sortPreference),
        });
    }, [isRelatedList, updateSearchParams]);

    const recordListSortOptions = useRecordListSort(currentTable?.name || "", {
        controlledSortPreference: urlSortState.sortPreference,
        hasControlledSortPreference: !isRelatedList && urlSortState.hasExplicitSort,
        onSortPreferenceChange: !isRelatedList ? handleSortPreferenceChange : undefined,
    });

    const queryKey = useMemo(
        () => isRelatedList
            ? queryKeys.records.list(currentTable?.name || "", "related", effectiveQueryParams, recordListSortOptions.sortPreference)
            : queryKeys.records.list(currentTable?.name || "", "list", effectiveQueryParams, recordListSortOptions.sortPreference),
        [currentTable?.name, effectiveQueryParams, recordListSortOptions.sortPreference, isRelatedList]
    );
    const handleReload = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey,
        });
    }, [queryClient, queryKey]);

    if (isLoading || recordListSortOptions.sortLoading) {
        return <RecordListSkeletonComponent />
    }

    if (!currentTable) {
        return <Translate variant="h2" text="No table found" align="center" />;
    }

    return (
        <RecordListProvider
            table={currentTable}
            queryParams={effectiveQueryParams ?? undefined}
            onReload={handleReload}
        >
            {currentTable && !isRelatedList && (
                <Box ref={toolbarRef} sx={{ mb: { xs: 0, sm: 1 }, position: "sticky", top: 0, zIndex: "appBar" }}>
                    <RecordListNavbarComponent
                        tableName={currentTable.name}
                        columns={tableColumns}
                        appliedFilter={appliedFilter}
                        onApplyFilter={handleApplyFilter}
                        onClearFilter={handleClearFilter}
                        sortPreferences={recordListSortOptions.sortPreference}
                        onApplySort={recordListSortOptions.applySortPreference}
                        columnPreferences={columnPreferences.preferences}
                        columnPreferencesLoading={columnPreferences.isLoading}
                        onSaveColumnPreferences={columnPreferences.savePreferences}
                    />
                </Box>
            )}
            {isRelatedList && headerContent ? (
                <RelatedListHeader headerContent={headerContent} columnPreferences={columnPreferences} />
            ) : headerContent}
            <Paper
                elevation={1}
                sx={{
                    marginBottom: 1,
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                    overflow: isRelatedList ? "hidden" : "visible",
                    ...(!isRelatedList && {
                        "--record-list-toolbar-height": `${toolbarHeight}px`,
                    }),
                }}
            >
                <RecordListTableComponent
                    currentTable={currentTable}
                    queryKey={queryKey}
                    actions={actions}
                    readOnly={readOnly}
                    columnLabels={columnLabels}
                    isRelatedList={isRelatedList}
                    sortOptions={recordListSortOptions}
                    tableColumns={tableColumns}
                    columnPreferences={columnPreferences.preferences}
                    columnPreferencesLoading={columnPreferences.isLoading}
                    hasActiveFilter={Boolean(appliedFilter)}
                    onClearFilter={handleClearFilter}
                />
            </Paper>
        </RecordListProvider>
    );
};

export default memo(RecordListComponent);
